import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowUpward, Stop, Mic, StopCircle } from '@mui/icons-material';
import { useLiveVoiceTranscription } from '../../hooks/useLiveVoiceTranscription';

interface ChatInputProps {
  onSend: (message: string, audioBase64?: string) => void;
    onCancel?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, onCancel, disabled, isStreaming }) => {
  const theme = useTheme();
  const [value, setValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const deepgramConnectionRef = useRef<any>(null);
  const finalTranscriptRef = useRef<string>('');

  const { getDeepgramSocketConnection } = useLiveVoiceTranscription();

  const startRecording = async () => {
    try {
      finalTranscriptRef.current = '';
      setLiveTranscript('');

      // Buffer chunks that arrive before the WebSocket is ready
      const pendingBlobs: Blob[] = [];
      let socketReady = false;

      let isConnectionOpen = false;
      try {
        isConnectionOpen = !!deepgramConnectionRef.current &&
          deepgramConnectionRef.current.getReadyState() === WebSocket.OPEN;
      } catch {
        // Stale connection — close and discard
        try { deepgramConnectionRef.current?.close(); } catch { /* ignore */ }
        deepgramConnectionRef.current = null;
      }

      // Kick off mic request; only fetch a new Deepgram connection if needed
      const streamPromise = navigator.mediaDevices.getUserMedia({ audio: true });
      const connectionPromise = isConnectionOpen
        ? Promise.resolve(deepgramConnectionRef.current)
        : getDeepgramSocketConnection();

      const [stream, connection] = await Promise.all([streamPromise, connectionPromise]);

      if (!isConnectionOpen) {
        deepgramConnectionRef.current = connection;

        // Register the message listener once per connection lifetime
        connection.on('message', (data: any) => {
          if (data.type === 'Results') {
            const transcript = data.channel?.alternatives?.[0]?.transcript ?? '';
            if (transcript) {
              if (data.is_final) {
                finalTranscriptRef.current += transcript + ' ';
                setLiveTranscript(finalTranscriptRef.current);
              } else {
                setLiveTranscript(finalTranscriptRef.current + transcript);
              }
            }
          }
        });

        connection.connect();
        await connection.waitForOpen();
      }

      // Connection is ready — flush any buffered blobs immediately after recording starts
      socketReady = true;

      const mimeType = 'audio/webm;codecs=opus';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          if (socketReady && deepgramConnectionRef.current) {
            deepgramConnectionRef.current.socket.send(event.data);
          } else {
            pendingBlobs.push(event.data);
          }
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      // Start capturing immediately
      mediaRecorder.start(250);
      setIsRecording(true);

      for (const blob of pendingBlobs) {
        deepgramConnectionRef.current!.socket.send(blob);
      }
      pendingBlobs.length = 0;

      timeoutRef.current = setTimeout(() => {
        stopRecording();
      }, 60000); // 1 minute timeout

    } catch (err) {
      console.error('Error accessing microphone:', err);
      setIsRecording(false);
      setLiveTranscript('');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const transcript = finalTranscriptRef.current.trim();
    if (transcript) {
      setValue(prev => prev ? `${prev} ${transcript}` : transcript);
    }
    setLiveTranscript('');
    finalTranscriptRef.current = '';
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (deepgramConnectionRef.current) {
        deepgramConnectionRef.current.close();
      }
    };
  }, []);

  const handleSend = () => {
    if (value.trim() && !disabled) {
      onSend(value);
      setValue('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: alpha(theme.palette.background.default, 0.8),
        backdropFilter: 'blur(8px)',
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
        }}
      >
        {isRecording && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ color: 'error.main', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              ● Recording
            </Typography>
            {liveTranscript && (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
                {liveTranscript}
              </Typography>
            )}
          </Box>
        )}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isRecording}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              pr: 1,
              fontSize: '0.9rem',
              '& fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              py: 1.5,
              px: 2,
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
            gap: 1,
          }}
        >
          <IconButton
            onClick={isRecording ? stopRecording : startRecording}
            disabled={disabled || isStreaming}
            sx={{
              color: isRecording ? 'white' : 'primary.main',
              bgcolor: isRecording ? 'error.main' : alpha(theme.palette.primary.main, 0.1),
              border: '1px solid',
              borderColor: isRecording ? 'error.main' : alpha(theme.palette.primary.main, 0.3),
              '&:hover': {
                bgcolor: isRecording
                  ? alpha(theme.palette.error.main, 0.85)
                  : alpha(theme.palette.primary.main, 0.2),
              },
              ...(isRecording && {
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.4)}` },
                  '50%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.error.main, 0)}` },
                },
              }),
            }}
          >
            {isRecording ? <StopCircle /> : <Mic />}
          </IconButton>

          {isStreaming && onCancel ? (
            <IconButton
              onClick={onCancel}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.2),
                },
              }}
            >
              <Stop fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              onClick={handleSend}
              disabled={!value.trim() || disabled || isRecording}
              sx={{
                bgcolor: '#cc5544',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#b54636',
                },
                '&.Mui-disabled': {
                  bgcolor: alpha('#cc5544', 0.3),
                  color: 'white',
                },
              }}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
